/**
 * Practice Page
 * Main math and word practice interface
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import Layout from '@components/layout/Layout'
import { useProblemController } from '@hooks/useProblemController'
import { useTimer } from '@hooks/useTimer'
import QuestionCard from '@components/practice/Flashcard/QuestionCard'
import AnswerCard from '@components/practice/Flashcard/AnswerCard'
import Timer from '@components/practice/Flashcard/Timer'
import ControlsBar from '@components/practice/ControlsBar'
import SettingsPanel from '@components/practice/Settings/SettingsPanel'
import LettersPrompt from '@components/practice/LettersPrompt'
import { useSpeech } from '@hooks/useSpeech'
import { LISTENING } from '@config/constants'
import {
  createBrowserRecognitionSession,
  isBrowserSpeechRecognitionAvailable,
  createAudioRecordingSession,
} from '@lib/listeningController'
import { evaluateTranscriptMatch } from '@lib/phonetic'
import { transcribeWithOpenAiBlob, RateLimitError } from '@lib/speechApi'

export default function Practice() {
  const [settingsOpen, setSettingsOpen] = useState(false)
  const {
    currentProblem,
    currentStreak,
    stats,
    settings,
    showFeedback,
    isCorrect,
    checkAnswer,
    nextProblem,
    skipProblem,
    prefetchNextProblem,
  } = useProblemController()
  const { speech, speakWithOpenAi, warmSpeechCache, clearSpeechCache, stopSpeechPlayback, isOpenAiSpeechEnabled } =
    useSpeech()

  const [isSpeaking, setIsSpeaking] = useState(false)
  const [speechError, setSpeechError] = useState<string | null>(null)
  const [listeningState, setListeningState] = useState<
    'idle' | 'recording' | 'transcribing' | 'success' | 'mismatch' | 'error'
  >('idle')
  const [listeningMessage, setListeningMessage] = useState('')
  const [heardTranscript, setHeardTranscript] = useState('')
  const listeningStatusTimerRef = useRef<number | null>(null)
  const lastAutoPlayWordRef = useRef<string | null>(null)
  const autoplayTimerRef = useRef<number | null>(null)
  const answerInputRef = useRef<HTMLInputElement>(null)

  // Track previous settings open state
  const [prevSettingsOpen, setPrevSettingsOpen] = useState(false)
  const settingsSnapshotRef = useRef<string | null>(null)

  // Regenerate problem when settings panel closes
  useEffect(() => {
    // Capture a snapshot when the panel opens
    if (settingsOpen && !prevSettingsOpen) {
      settingsSnapshotRef.current = JSON.stringify(settings)
    }

    // If the panel just closed, regenerate only when settings changed
    if (prevSettingsOpen && !settingsOpen) {
      const previousSettings = settingsSnapshotRef.current
      const currentSettings = JSON.stringify(settings)
      if (previousSettings !== null && previousSettings !== currentSettings) {
        nextProblem()
      }
    }
    setPrevSettingsOpen(settingsOpen)
  }, [settingsOpen, nextProblem, prevSettingsOpen, settings])

  // Determine which timer to use (math or word)
  const isWordOrLetters =
    currentProblem?.operationId === 'wordPractice' || currentProblem?.operationId === 'letters'
  const timerDuration = isWordOrLetters ? settings.timers.word : settings.timers.math

  const timer = useTimer(timerDuration, () => {
    // Auto-skip when timer expires
    skipProblem()
  })

  // Start timer when new problem appears
  // Note: We intentionally list timer methods individually rather than the timer object
  // to avoid infinite re-renders (the timer object is recreated each render)
  useEffect(() => {
    if (!currentProblem || !timer.hasTimer) {
      return
    }

    timer.reset()
    timer.start()

    return () => {
      timer.stop()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProblem, timer.hasTimer, timer.reset, timer.start, timer.stop])

  const clearListeningStatusTimer = useCallback(() => {
    if (listeningStatusTimerRef.current) {
      clearTimeout(listeningStatusTimerRef.current)
      listeningStatusTimerRef.current = null
    }
  }, [])

  const resetListeningUi = useCallback(() => {
    clearListeningStatusTimer()
    setListeningState('idle')
    setListeningMessage('')
    setHeardTranscript('')
  }, [clearListeningStatusTimer])

  const speakWithBrowser = useCallback((text: string): Promise<void> => {
    return new Promise((resolve) => {
      if (!text) {
        resolve()
        return
      }
      window.speechSynthesis.cancel()
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.rate = 0.85
      utterance.pitch = 1.0
      utterance.volume = 1.0
      utterance.onend = () => resolve()
      utterance.onerror = () => resolve()
      window.speechSynthesis.speak(utterance)
    })
  }, [])

  useEffect(() => {
    return () => {
      clearListeningStatusTimer()
      stopSpeechPlayback()
      if (autoplayTimerRef.current) {
        clearTimeout(autoplayTimerRef.current)
      }
    }
  }, [clearListeningStatusTimer, stopSpeechPlayback])

  // Determine if we're in spelling mode (word practice levels 6-10)
  const isSpellingMode = currentProblem?.metadata?.mode === 'spelling'
  const isLettersMode = currentProblem?.operationId === 'letters'
  const wordToSpeak =
    isSpellingMode && currentProblem?.operationId === 'wordPractice'
      ? currentProblem?.metadata?.word ||
        (typeof currentProblem?.answer === 'string' ? String(currentProblem.answer) : undefined)
      : undefined
  const listeningAvailable =
    speech.listenEnabled &&
    currentProblem?.operationId === 'wordPractice' &&
    !isSpellingMode &&
    settings.wordPractice.difficulty <= LISTENING.DEFAULT_MAX_DIFFICULTY

  useEffect(() => {
    resetListeningUi()
  }, [currentProblem?.prompt, listeningAvailable, resetListeningUi])

  const handleSpeechConfigChange = useCallback(() => {
    clearSpeechCache()
    stopSpeechPlayback()
    if (
      currentProblem?.operationId === 'wordPractice' &&
      wordToSpeak &&
      isOpenAiSpeechEnabled() &&
      settings.wordPractice.difficulty >= 6
    ) {
      warmSpeechCache(wordToSpeak)
    }
  }, [
    clearSpeechCache,
    currentProblem?.operationId,
    isOpenAiSpeechEnabled,
    settings.wordPractice.difficulty,
    stopSpeechPlayback,
    warmSpeechCache,
    wordToSpeak,
  ])

  const handleSpeakWord = useCallback(async () => {
    if (!wordToSpeak || settings.wordPractice.difficulty < 6) return
    setIsSpeaking(true)
    try {
      if (isOpenAiSpeechEnabled()) {
        await speakWithOpenAi(wordToSpeak)
        setSpeechError(null)
      } else {
        await speakWithBrowser(wordToSpeak)
      }
    } catch (error) {
      console.warn('OpenAI speech failed, falling back to browser', error)
      if (error instanceof RateLimitError) {
        setSpeechError(`Rate limit reached. Resets in ${error.getTimeUntilReset()}. Using browser voice.`)
      } else {
        setSpeechError('Using browser voice because OpenAI speech was unavailable.')
      }
      await speakWithBrowser(wordToSpeak)
    } finally {
      setIsSpeaking(false)
      // Focus the input after manually playing the word
      setTimeout(() => {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur()
        }
        if (answerInputRef.current) {
          answerInputRef.current.focus()
        }
      }, 100)
    }
  }, [isOpenAiSpeechEnabled, speakWithBrowser, speakWithOpenAi, wordToSpeak, settings.wordPractice.difficulty])

  // Pre-warm OpenAI speech for current and next word
  useEffect(() => {
    if (
      currentProblem?.operationId !== 'wordPractice' ||
      !wordToSpeak ||
      !isOpenAiSpeechEnabled() ||
      settings.wordPractice.difficulty < 6
    ) {
      return
    }
    warmSpeechCache(wordToSpeak)
    const nextPrefetch = prefetchNextProblem()
    if (nextPrefetch?.metadata?.word) {
      warmSpeechCache(nextPrefetch.metadata.word)
    } else if (
      nextPrefetch?.operationId === 'wordPractice' &&
      typeof nextPrefetch?.answer === 'string'
    ) {
      warmSpeechCache(String(nextPrefetch.answer))
    }
  }, [
    currentProblem?.operationId,
    isOpenAiSpeechEnabled,
    prefetchNextProblem,
    settings.wordPractice.difficulty,
    warmSpeechCache,
    wordToSpeak,
  ])

  // Auto-play each new word practice prompt after a short delay
  useEffect(() => {
    if (
      !currentProblem ||
      currentProblem.operationId !== 'wordPractice' ||
      !wordToSpeak ||
      settings.wordPractice.difficulty < 6
    ) {
      return
    }

    const playbackKey = `${currentProblem.prompt}-${wordToSpeak}`
    if (lastAutoPlayWordRef.current === playbackKey) {
      return
    }
    lastAutoPlayWordRef.current = playbackKey

    if (autoplayTimerRef.current) {
      clearTimeout(autoplayTimerRef.current)
    }

    autoplayTimerRef.current = window.setTimeout(async () => {
      try {
        if (isOpenAiSpeechEnabled()) {
          await speakWithOpenAi(wordToSpeak)
        } else {
          await speakWithBrowser(wordToSpeak)
        }
      } catch (error) {
        console.warn('Autoplay failed, falling back to browser voice.', error)
        await speakWithBrowser(wordToSpeak)
      }
    }, 500)
  }, [
    currentProblem,
    isOpenAiSpeechEnabled,
    settings.wordPractice.difficulty,
    speakWithBrowser,
    speakWithOpenAi,
    wordToSpeak,
  ])

  // Focus input after new problem loads (for free entry mode)
  useEffect(() => {
    const mode =
      currentProblem?.operationId === 'wordPractice' || currentProblem?.operationId === 'letters'
        ? 'free'
        : settings.mode
    if (mode !== 'free' || !currentProblem) return

    // For word practice with speech, wait for speech to complete
    const delay = wordToSpeak ? 1500 : 100

    const focusTimer = setTimeout(() => {
      if (answerInputRef.current) {
        answerInputRef.current.focus()
      }
    }, delay)

    return () => {
      clearTimeout(focusTimer)
    }
  }, [currentProblem, wordToSpeak, settings.mode])

  const handleTranscriptMatch = useCallback(
    (transcript: string) => {
      if (!currentProblem) return
      const match = evaluateTranscriptMatch(transcript, String(currentProblem.answer))
      setHeardTranscript(transcript)
      if (['exact', 'homophone', 'phonetic'].includes(match.status)) {
        setListeningState('success')
        setListeningMessage(`Heard “${transcript}”`)
        checkAnswer(currentProblem.answer)
        clearListeningStatusTimer()
        listeningStatusTimerRef.current = window.setTimeout(() => {
          setListeningMessage('')
        }, LISTENING.DEFAULT_IDLE_TIMEOUT)
      } else {
        setListeningState('mismatch')
        setListeningMessage(`Heard “${transcript}”. Try again?`)
      }
    },
    [checkAnswer, clearListeningStatusTimer, currentProblem]
  )

  const listeningSessionRef = useRef<{ stop: () => void } | null>(null)

  const handleStartListening = useCallback(async () => {
    if (!currentProblem || !listeningAvailable) return

    // If we're already recording, stop early and process
    if (listeningState === 'recording' && listeningSessionRef.current) {
      listeningSessionRef.current.stop()
      return
    }

    setListeningState('recording')
    setListeningMessage('Listening...')
    setHeardTranscript('')

    try {
      if (speech.listenPreferBrowser || !speech.key) {
        if (!isBrowserSpeechRecognitionAvailable()) {
          throw new Error('Browser listening not available on this device.')
        }
        const session = createBrowserRecognitionSession({
          idleTimeoutMs: Math.min(LISTENING.DEFAULT_IDLE_TIMEOUT, 6000),
          maxDurationMs: 6000,
        })
        listeningSessionRef.current = session
        const result = await session.promise
        listeningSessionRef.current = null
        handleTranscriptMatch(result.transcript)
        return
      }

      const session = await createAudioRecordingSession({ maxDurationMs: 6000 })
      listeningSessionRef.current = session
      const blob = await session.promise
      listeningSessionRef.current = null

      setListeningState('transcribing')
      setListeningMessage('Transcribing...')
      const { text } = await transcribeWithOpenAiBlob({
        blob,
        apiKey: speech.key,
        prompt: `The expected word is "${currentProblem.answer}".`,
        useProxy: speech.useProxy,
      })
      handleTranscriptMatch(text)
    } catch (error) {
      listeningSessionRef.current = null
      setListeningState('error')
      if (error instanceof RateLimitError) {
        setListeningMessage(`Rate limit reached. Try again in ${error.getTimeUntilReset()}.`)
      } else {
        setListeningMessage(
          error instanceof Error ? error.message : 'Listening failed. Please retry.'
        )
      }
    }
  }, [
    currentProblem,
    handleTranscriptMatch,
    listeningAvailable,
    listeningState,
    speech.key,
    speech.listenPreferBrowser,
    speech.useProxy,
  ])

  const lettersLevel = isLettersMode
    ? Number(currentProblem?.metadata?.lettersLevel || settings.lettersPractice.level)
    : null
  const lettersPattern =
    isLettersMode && typeof currentProblem?.metadata?.lettersPattern === 'string'
      ? currentProblem.metadata.lettersPattern
      : isLettersMode && typeof currentProblem?.prompt === 'string'
        ? currentProblem.prompt
        : null
  const lettersWord =
    isLettersMode && typeof currentProblem?.metadata?.word === 'string'
      ? currentProblem.metadata.word
      : null
  const lettersQuestion =
    isLettersMode && lettersWord && lettersPattern ? (
      <LettersPrompt word={lettersWord} pattern={lettersPattern} level={lettersLevel ?? 1} />
    ) : (
      currentProblem?.prompt ?? ''
    )
  const questionContentKey =
    currentProblem?.metadata?.promptKey ??
    `${typeof currentProblem?.prompt === 'string' ? currentProblem.prompt : 'letters'}-${currentProblem?.metadata?.word ?? currentProblem?.answer ?? ''}`
  const lettersPlaceholder = undefined

  const effectiveMode =
    currentProblem?.operationId === 'wordPractice' || currentProblem?.operationId === 'letters'
      ? 'free'
      : settings.mode

  return (
    <Layout heroImage="/images/hero/practice-hero.jpg" heroAlt="Practice Station">
      <div className="practice-page">
        <h1>Practice Station</h1>

        <ControlsBar
          stats={stats}
          currentStreak={currentStreak}
          onSettingsToggle={() => setSettingsOpen(prev => !prev)}
          settingsOpen={settingsOpen}
        />

        <SettingsPanel
          isOpen={settingsOpen}
          onSpeechConfigChange={handleSpeechConfigChange}
          onClose={() => setSettingsOpen(false)}
        />

        <main className="practice__container">
          {currentProblem ? (
            <div className="flashcard">
              <QuestionCard
                question={lettersQuestion}
                fontSize={settings.fonts.question}
                contentKey={questionContentKey}
                isSpellingMode={isSpellingMode}
                wordToSpeak={wordToSpeak}
                onSpeakWord={handleSpeakWord}
                isSpeaking={isSpeaking}
              />
              {speechError && <p className="flashcard__hint">{speechError}</p>}

              <Timer
                timeRemaining={timer.timeRemaining}
                isRunning={timer.isRunning}
                hasTimer={timer.hasTimer}
              />

              <AnswerCard
                mode={effectiveMode}
                options={currentProblem.options}
                fontSize={settings.fonts.answer}
                onSubmit={checkAnswer}
                onSkip={nextProblem}
                showFeedback={showFeedback}
                isCorrect={isCorrect}
                disabled={showFeedback && isCorrect}
                isSpellingMode={isSpellingMode}
                listeningAvailable={listeningAvailable}
                listeningState={listeningState}
                listeningMessage={listeningMessage}
                heardTranscript={heardTranscript}
                onStartListening={handleStartListening}
                inputRef={answerInputRef}
                inputPlaceholder={lettersPlaceholder}
              />
            </div>
          ) : (
            <div className="flashcard">
              <QuestionCard
                question="Loading..."
                fontSize={settings.fonts.question}
                contentKey="loading"
              />
              <Timer timeRemaining={0} isRunning={false} hasTimer={false} />
            </div>
          )}
        </main>
      </div>
    </Layout>
  )
}
